import React from "react";
import { CHAT_COLUMN_CLASS } from "../constants/chatLayout";
import { useAuth } from "../../../../context/AuthContext";
import { CognitoAuthService } from "../../../../services/cognitoService";

/**
 * Reassurance card shown while generate_image_batch runs (30-180s, like deep
 * research). Confirms images are saving to the Media Library in the background
 * and that a completion email is on the way — informational only, always on,
 * no opt-out.
 */
export const BulkImageProgressCard: React.FC = () => {
  // AuthContext's userId is NOT reliably the email — on a rehydrated session
  // (page load/token refresh) it's set to the Cognito username/sub, a UUID,
  // not the email (only set to the real email right after interactive login).
  // getUserEmail() reads the token's own "email" claim, matching what the
  // backend actually sends the completion email to.
  const { idToken } = useAuth();
  const userEmail = CognitoAuthService.getUserEmail(idToken);

  return (
    <div className={`${CHAT_COLUMN_CLASS} animate-fadeIn`}>
      <div className="w-full min-w-0">
        <div className="rounded-2xl border border-theme-border bg-theme-surface-ui p-4 space-y-3">
          <div className="flex items-center gap-3">
            <div className="relative h-9 w-9 shrink-0">
              <div className="absolute inset-0 animate-spin rounded-full border-2 border-theme-accent/30 border-t-theme-accent" />
              <div className="absolute inset-0 flex items-center justify-center">
                <svg className="h-4 w-4 text-theme-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                    d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14M14 8h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-theme-fg leading-snug">
                Generating your images…
              </p>
              <p className="text-xs text-theme-fg-muted mt-0.5">
                This takes about 1–2 minutes. You can stay here, browse other pages, or
                close this tab — everything is saved directly to your Media Library
                {userEmail ? (
                  <> and we'll email you at <span className="font-medium text-theme-fg">{userEmail}</span> once it's ready.</>
                ) : (
                  <> and we'll email you once it's ready.</>
                )}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 rounded-lg border border-theme-border bg-theme-surface px-3 py-2">
            <svg className="h-4 w-4 shrink-0 text-theme-fg-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            <span className="text-xs font-medium text-theme-fg">
              We'll notify you by email when this batch is ready
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
